import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtTL } from "@/lib/format";

// Steel density 7.85 kg/dm³ = 0.00000785 kg/mm³
export const STEEL_DENSITY = 0.00000785;

// Modular profile config. Add new steel product types here with their input fields.
// field tuple: [itemKey, label]
export const PROFILE_TYPES = {
  sac: {
    label: "Sac (otomatik kg)",
    fields: [["thickness", "Kalınlık (mm)"], ["width", "En (mm)"], ["length", "Boy (mm)"]],
  },
  lama: {
    label: "Lama (otomatik kg)",
    fields: [["thickness", "Kalınlık (mm)"], ["width", "Genişlik (mm)"], ["length", "Boy (mm)"]],
  },
  kosebent: {
    label: "Köşebent (otomatik kg)",
    fields: [["width", "Kanat/Kol (mm)"], ["thickness", "Et Kalınlığı (mm)"], ["length", "Boy (mm)"]],
  },
  kutu: {
    label: "Kutu Profil (otomatik kg)",
    fields: [["width", "Genişlik (mm)"], ["height", "Yükseklik (mm)"], ["thickness", "Et Kalınlığı (mm)"], ["length", "Uzunluk (mm)"]],
  },
  boru: {
    label: "Yuvarlak Boru (otomatik kg)",
    fields: [["width", "Dış Çap (mm)"], ["thickness", "Et Kalınlığı (mm)"], ["length", "Uzunluk (mm)"]],
  },
  mil: {
    label: "Dolu Mil (otomatik kg)",
    fields: [["width", "Çap (mm)"], ["length", "Uzunluk (mm)"]],
  },
};

export const isSteelType = (t) => !!PROFILE_TYPES[t];

// Per-piece kg for a given steel type. Returns 0 when dimensions are incomplete/invalid.
export function computeUnitKg(type, it) {
  const t = Number(it.thickness) || 0, w = Number(it.width) || 0, h = Number(it.height) || 0, l = Number(it.length) || 0;
  let area = 0; // cross-section area in mm²
  if (type === "sac" || type === "lama") area = t * w;
  else if (type === "kosebent") area = (2 * w - t) * t;
  else if (type === "kutu") area = w * h - Math.max(0, w - 2 * t) * Math.max(0, h - 2 * t);
  else if (type === "boru") { const din = Math.max(0, w - 2 * t); area = (Math.PI / 4) * (w * w - din * din); }
  else if (type === "mil") area = (Math.PI / 4) * w * w;
  return area > 0 && l > 0 ? area * l * STEEL_DENSITY : 0;
}

export const isSteelValid = (it) => isSteelType(it.calc_type) && computeUnitKg(it.calc_type, it) > 0 && Number(it.quantity) > 0;

export const round2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;

// Recalculate an item in place. `discount` handling is caller-dependent (quotes use per-item discount).
// fire_pct (%) adds a waste allowance that increases the billed weight (and thus the cost).
export function recalcItem(it) {
  const r = { ...it };
  const qty = Number(r.quantity) || 0, price = Number(r.unit_price) || 0;
  let gross;
  if (isSteelType(r.calc_type)) {
    const fire = Number(r.fire_pct) || 0;
    r.unit = "kg";
    r.unit_kg = computeUnitKg(r.calc_type, r);
    r.net_kg = r.unit_kg * qty;
    r.total_kg = r.net_kg * (1 + fire / 100);
    r.kilogram = r.total_kg;
    gross = r.unit_kg > 0 && qty > 0 ? r.total_kg * price : 0;
  } else {
    gross = qty * price;
  }
  // İskonto: yüzde verilmişse mutlak tutara çevrilir (KDV/iskonto modu); yoksa mutlak indirim kullanılır.
  if (Number(r.discount_pct) > 0) r.discount = round2(gross * (Number(r.discount_pct) / 100));
  r.total = round2(Math.max(0, gross - (Number(r.discount) || 0)));
  return r;
}

export const steelTypeOptions = () =>
  Object.entries(PROFILE_TYPES).map(([value, cfg]) => ({ value, label: cfg.label }));

const fmtKg = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Reusable dimension editor grid for steel items. Embeddable in both sales & quote forms.
export function SteelDimsEditor({ item, onPatch, idPrefix = "steel" }) {
  const cfg = PROFILE_TYPES[item.calc_type];
  if (!cfg) return null;
  const valid = isSteelValid(item);
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 bg-muted/30 p-2 rounded-md">
        {cfg.fields.map(([key, label]) => (
          <div key={key}>
            <Label className="text-xs text-muted-foreground">{label}</Label>
            <Input type="number" step="0.01" value={item[key] || ""} onChange={(e) => onPatch({ [key]: Number(e.target.value) })}
              className="h-9 mt-1 text-right" data-testid={`${idPrefix}-${key}`} />
          </div>
        ))}
        <div>
          <Label className="text-xs text-muted-foreground">Adet</Label>
          <Input type="number" step="1" value={item.quantity} onChange={(e) => onPatch({ quantity: Number(e.target.value) })}
            className="h-9 mt-1 text-right" data-testid={`${idPrefix}-qty`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Fire (%)</Label>
          <Input type="number" step="0.1" value={item.fire_pct || ""} placeholder="0" onChange={(e) => onPatch({ fire_pct: Number(e.target.value) })}
            className="h-9 mt-1 text-right" data-testid={`${idPrefix}-fire`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Toplam KG</Label>
          <div className="h-9 mt-1 flex items-center justify-end px-2 rounded-md bg-muted/60 num font-semibold" data-testid={`${idPrefix}-totalkg`}>
            {valid ? `${fmtKg(item.total_kg)} kg` : "—"}
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">KG Fiyatı (₺)</Label>
          <Input type="number" step="0.01" value={item.unit_price} onChange={(e) => onPatch({ unit_price: Number(e.target.value) })}
            className="h-9 mt-1 text-right" data-testid={`${idPrefix}-kgprice`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Toplam (₺)</Label>
          <div className="h-9 mt-1 flex items-center justify-end px-2 rounded-md bg-muted/60 num font-bold text-primary" data-testid={`${idPrefix}-total`}>
            {valid ? fmtTL(item.total) : "—"}
          </div>
        </div>
      </div>
      {valid ? (
        <div className="text-xs text-muted-foreground num">
          Birim: {fmtKg(item.unit_kg)} kg × {item.quantity} adet = {fmtKg(item.net_kg ?? item.unit_kg * (Number(item.quantity) || 0))} kg
          {Number(item.fire_pct) > 0 ? ` (+%${item.fire_pct} fire → ${fmtKg(item.total_kg)} kg)` : ""}
          {" · "}{fmtKg(item.total_kg)} kg × ₺{Number(item.unit_price || 0).toLocaleString("tr-TR")} = {fmtTL(item.total)}
        </div>
      ) : (
        <div className="text-xs text-amber-500" data-testid={`${idPrefix}-warning`}>
          Ölçüleri (mm) eksiksiz girin — KG ve tutar otomatik hesaplanacak.
        </div>
      )}
    </div>
  );
}
